import React, { useMemo, useCallback, useContext } from 'react';
import Toggle from '@atlaskit/toggle';
import FilterIcon from '@atlaskit/icon/glyph/filter';
import Lozenge from '@atlaskit/lozenge';
import Button from '@atlaskit/button';
import { useQueryParams } from 'use-query-params';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';

import { SearchSettingsDropdown } from '@/components/primitive';
import DraftStatusLozenge from '@/components/composed/slo-card/lozenges/draft-status-lozenge';
import {
  flex,
  flexStart,
  marginPushRight,
  spaceGapComfortable,
  spaceGapCompact,
  spaceGapMinimum,
  spaceTopCozy,
} from '@/styles';
import { filterStore, useLocationController } from '@/controllers';
import {
  EventMetricsKeys,
  EventMetricsTypes,
  sendAnalyticsEvent,
} from '@/common/ufo/analytics-events';

import { filterStyles, filterTitleStyles } from './styles';

type SLOFilterBarProps = {
  basicMode?: boolean;
};

const SLOFilterBar: React.FC<SLOFilterBarProps> = ({ basicMode }) => {
  const { isTeamPageCapabilitiesTab, isProductPageCapabilitiesTab } = useLocationController();

  const { search } = useLocation();
  const { showDrafts, hasBreachStatusOnly, hasQualityIssuesOnly } = useMemo(
    () => qs.parse(search),
    [search]
  );
  const [, setQuery] = useQueryParams({});
  const hasAnyFilterApplied = !showDrafts || hasBreachStatusOnly || hasQualityIssuesOnly;

  const handleResetFilters = useCallback(() => {
    setQuery({
      showDrafts: true,
      hasBreachStatusOnly: undefined,
      hasQualityIssuesOnly: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShowDraftsFilterChange = useCallback(() => {
    setQuery({
      showDrafts: showDrafts ? undefined : true,
    });
    sendAnalyticsEvent(EventMetricsTypes.UI, EventMetricsKeys.DashboardFilterBarToggledDraft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDrafts]);

  const handleHasBreachStatusFilterChange = useCallback(() => {
    setQuery({
      hasBreachStatusOnly: hasBreachStatusOnly ? undefined : true,
    });
    sendAnalyticsEvent(EventMetricsTypes.UI, EventMetricsKeys.DashboardFilterBarToggledBreach);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBreachStatusOnly]);

  const handleHasQualityIssueOnlyFilterChange = useCallback(() => {
    setQuery({
      hasQualityIssuesOnly: hasQualityIssuesOnly ? undefined : true,
    });
    sendAnalyticsEvent(
      EventMetricsTypes.UI,
      EventMetricsKeys.DashboardFilterBarToggledQualityIssue
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasQualityIssuesOnly]);

  // Set the filter state based on the query params and note that the source of
  // truth is the URL
  const { actions: filterActions } = useContext(filterStore);
  React.useEffect(() => {
    filterActions.setSLOShowDrafts(showDrafts === 'true');
    filterActions.setSLOHasBreachingOnly(hasBreachStatusOnly === 'true');
    filterActions.setSLOHasQualityIssuesOnly(hasQualityIssuesOnly === 'true');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDrafts, hasBreachStatusOnly, hasQualityIssuesOnly]);

  return (
    <div css={[flexStart, spaceGapComfortable, spaceTopCozy, filterStyles]}>
      <div css={[flexStart, spaceGapMinimum]}>
        <FilterIcon size="small" label="filter" />
        <span css={filterTitleStyles}>filters</span>
      </div>
      <div css={[flexStart, spaceGapComfortable]}>
        {!basicMode && (
          <>
            <div css={[flexStart, spaceGapMinimum]}>
              <span>
                With open SLO breaches <Lozenge>only</Lozenge>
              </span>
              <Toggle
                testId="toggle-slo-show-with-breaching"
                onChange={handleHasBreachStatusFilterChange}
                isChecked={hasBreachStatusOnly === 'true'}
              />
            </div>
            <div css={[flexStart, spaceGapMinimum]}>
              <span>
                With SLO quality issues <Lozenge>only</Lozenge>
              </span>
              <Toggle
                testId="toggle-slo-show-with-quality-issues"
                onChange={handleHasQualityIssueOnlyFilterChange}
                isChecked={hasQualityIssuesOnly === 'true'}
              />
            </div>
          </>
        )}
        <div css={[flexStart, spaceGapMinimum]}>
          <span>
            Hide <DraftStatusLozenge /> SLOs
          </span>
          <Toggle
            testId="toggle-slo-show-drafts"
            onChange={handleShowDraftsFilterChange}
            isChecked={showDrafts !== 'true'}
          />
        </div>
      </div>
      <div css={[marginPushRight, flex, spaceGapCompact]}>
        <Button appearance="subtle" isDisabled={!hasAnyFilterApplied} onClick={handleResetFilters}>
          Reset filters
        </Button>
        <SearchSettingsDropdown
          isDisabled={!(isTeamPageCapabilitiesTab || isProductPageCapabilitiesTab)}
          isSearchViewTypeDisabled
        />
      </div>
    </div>
  );
};

export default SLOFilterBar;
